// /w/:wid/mail/:id — one conversation: the band (back · subject · prev/next), who it's between, the letters (yours
// indented on the right, theirs framed), "17 days later" gaps, and the reply dock (trash · Write a reply… · REPLY).
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { WorldPage, useWorldCity } from "@/app/WorldPage";
import { art } from "@/kit/art";
import { StickyBar } from "@/kit/city";
import { IconChevronDown, IconChevronLeft, IconChevronUp, IconQuill, IconServer, IconTrash } from "@/kit/icons";
import { Confirm } from "@/kit/overlays";
import { Button, Card, Disc, Empty, Medallion, Skeleton, cx } from "@/kit/primitives";
import { useSession } from "@/state/session";
import { usePrompts } from "@/state/prompts";
import { useMail, useMailMutations, useMailThread } from "@/state/socialQueries";
import { ownVillages } from "@/state/queries";
import { usePlayerInfo } from "@/state/mapQueries";
import { fmtCoords, plural } from "@/game/format";
import { fmtClock, fmtDate } from "@/game/time";
import { MAIL_MAX_BODY, gapLabel, isMine, mailItems, matchesTab, toMs, type MailTab } from "@/game/mail";
import "../social.css";

export function MailThread() {
  return (
    <WorldPage>
      <Thread />
    </WorldPage>
  );
}

const stampOf = (ms: number) => (Number.isFinite(ms) ? `${String(new Date(ms).getUTCMonth() + 1).padStart(2, "0")}/${String(new Date(ms).getUTCDate()).padStart(2, "0")}/${String(new Date(ms).getUTCFullYear()).slice(2)} · ${fmtClock(ms)}` : "—");

export function Thread() {
  const { id = "" } = useParams();
  const { wid, v, base } = useWorldCity();
  const [params] = useSearchParams();
  const tab = (params.get("t") ?? "all") as MailTab;
  const nav = useNavigate();
  const session = useSession();
  const list = useMail(wid);
  const thread = useMailThread(wid, id);
  const { reply, remove, markRead } = useMailMutations(wid);
  const [text, setText] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const bottom = useRef<HTMLDivElement>(null);

  const items = useMemo(() => mailItems(list.data, session.uid).filter((m) => matchesTab(m, tab)), [list.data, session.uid, tab]);
  const index = items.findIndex((m) => m.id === id);
  const meta = index >= 0 ? items[index] : mailItems(list.data, session.uid).find((m) => m.id === id) ?? null;
  const letters = thread.data?.output?.mail ?? [];
  const otherLetter = letters.find((l) => !isMine(l, session.uid));
  const otherId = otherLetter ? Number(otherLetter.from_id) : meta?.otherId ?? -1;
  const otherName = meta?.other ?? otherLetter?.from_login ?? "…";
  const system = !!meta?.system || letters.some((l) => Number(l.no_reply) === 1);
  const other = usePlayerInfo(wid, otherId > 0 ? otherId : null);
  const otherCity = Object.values(other.data?.player_details ?? {})[0]?.target_char?.villages?.[0];
  const myCity = ownVillages(v, session.uid)[0] ?? (v?.village ? { city_name: v.village.city_name, coords: v.village.coords } : undefined);

  // the server marked it read when it answered the thread — reflect that in the list
  useEffect(() => {
    if (thread.data && meta?.unread) markRead(id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [thread.dataUpdatedAt]);
  useEffect(() => {
    bottom.current?.scrollIntoView({ block: "end" });
  }, [letters.length]);

  const go = (i: number) => { const t = items[i]; if (t) nav(`${base}/mail/${t.id}${tab === "all" ? "" : `?t=${tab}`}`, { replace: true }); };
  const back = () => nav(`${base}/mail${tab === "all" ? "" : `?t=${tab}`}`);
  const send = async () => {
    const body = text.trim();
    if (!body) return;
    try {
      await reply.mutateAsync({ id, body });
      setText("");
      usePrompts.getState().toast("Reply sent.", "success");
    } catch (e) {
      usePrompts.getState().error(e);
    }
  };
  const doDelete = async () => {
    setConfirmDelete(false);
    try {
      await remove.mutateAsync([id]);
      usePrompts.getState().toast("Conversation deleted.", "success");
      back();
    } catch (e) {
      usePrompts.getState().error(e);
    }
  };

  const last = letters[letters.length - 1];
  const first = letters[0];
  const waiting = last ? (isMine(last, session.uid) ? `Sent · waiting on ${otherName}` : system ? "" : `${otherName} is waiting on your reply`) : "";

  return (
    <div className="stack">
      <div className="tbar rp-tbar">
        <Disc size="sm" className="split-back" label="Back to mail" onClick={back}><IconChevronLeft /></Disc>
        <div className="tbar-mid">
          <h1><span className="t">{meta?.subject ?? "Letter"}</span></h1>
          {index >= 0 && <div className="tbar-sub rp-pos">{index + 1} of {items.length}</div>}
        </div>
        <div className="rp-steps">
          <Disc size="sm" label="Newer conversation" disabled={index <= 0} onClick={() => go(index - 1)}><IconChevronUp /></Disc>
          <Disc size="sm" label="Older conversation" disabled={index < 0 || index >= items.length - 1} onClick={() => go(index + 1)}><IconChevronDown /></Disc>
        </div>
      </div>

      <Card plain className="ml-between">
        <span className="medals">
          <Medallion src={art.avatar} alt="" size={44} />
          {system ? <Medallion size={44} className="rp-barb"><IconServer /></Medallion> : <Medallion initial={otherName} size={44} />}
        </span>
        <span className="txt">Between <b>you</b> and <b>{otherName}</b></span>
        <span className="meta">{letters.length ? `${plural(letters.length, "letter")}${first ? ` · since ${fmtDate(toMs(first.time))}` : ""}` : thread.isLoading ? "Reading…" : ""}</span>
      </Card>

      {thread.isLoading && !thread.data && <><Skeleton h={160} /><Skeleton h={160} /></>}
      {thread.isError && <Card><Empty>Couldn't open this conversation — it may have been deleted. <Button size="sm" onClick={() => void thread.refetch()}>Try again</Button></Empty></Card>}

      <div className="ml-thread">
        {letters.map((l, i) => {
          const mine = isMine(l, session.uid);
          const t = toMs(l.time);
          const gap = i > 0 ? gapLabel(toMs(letters[i - 1].time), t) : "";
          const city = mine ? myCity : otherCity;
          return (
            <div key={`${l.time}-${i}`}>
              {gap && <div className="ml-gap">{gap}</div>}
              {mine ? (
                <article className="ml-letter mine" aria-label={`Letter from you, ${stampOf(t)}`}>
                  <LetterHead name={session.login ?? "You"} you avatar={art.avatar} city={city ? `${city.city_name} ${fmtCoords(city.coords)}` : undefined} when={stampOf(t)} />
                  <p className="ml-body">{l.msg}</p>
                </article>
              ) : (
                <Card className="ml-letter theirs" aria-label={`Letter from ${l.from_login}, ${stampOf(t)}`}>
                  <LetterHead name={l.from_login} system={Number(l.no_reply) === 1} city={city ? `${city.city_name} ${fmtCoords(city.coords)}` : Number(l.no_reply) === 1 ? "The keepers of Valor" : undefined} when={stampOf(t)} />
                  <p className="ml-body">{l.msg}</p>
                </Card>
              )}
            </div>
          );
        })}
        {waiting && <div className="ml-gap">{waiting}</div>}
        <div ref={bottom} />
      </div>

      <StickyBar className="ml-dock">
        <div className="acts">
          <Disc size="sm" label="Delete this conversation" className="rp-trash" onClick={() => setConfirmDelete(true)}><IconTrash /></Disc>
          {system ? (
            <span className="c-note left" style={{ margin: 0 }}>Valor's letters don't take replies.</span>
          ) : (
            <textarea className="input" rows={1} value={text} maxLength={MAIL_MAX_BODY} placeholder="Write a reply…" aria-label="Your reply" onChange={(e) => setText(e.target.value)} disabled={reply.isPending} />
          )}
          {system ? (
            <Button kind="steel" size="md" onClick={() => nav(`${base}/mail/new`)}><IconQuill /> New</Button>
          ) : (
            <Button kind="crimson" size="md" busy={reply.isPending} disabled={!text.trim()} onClick={() => void send()}>Reply</Button>
          )}
        </div>
      </StickyBar>

      <Confirm open={confirmDelete} onClose={() => setConfirmDelete(false)} title="Delete this conversation?" text={`It leaves your mailbox (${otherName} keeps their copy). A reply from them brings it back.`} action="Delete" onConfirm={() => void doDelete()} busy={remove.isPending} />
    </div>
  );
}

function LetterHead({ name, you, system, avatar, city, when }: { name: string; you?: boolean; system?: boolean; avatar?: string; city?: string; when: string }) {
  return (
    <header className="ml-letterhead">
      {avatar ? <Medallion src={avatar} alt="" size={52} /> : system ? <Medallion size={52} className="rp-barb"><IconServer /></Medallion> : <Medallion initial={name} size={52} />}
      <div className="txt">
        <b>{name}{you && <small>you</small>}</b>
        {city && <span>{city}</span>}
      </div>
      <time className={cx(!city && "solo")}>{when}</time>
    </header>
  );
}
